import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Users, Search, Filter, X, CalendarDays, Globe } from "lucide-react";
import { Spinner, EmptyState, StatusBadge } from "../../components/common";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtINR, fmtDate } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { StaffLabel } from "../../components/StaffAvatar";
import { useAuth } from "../../context/AuthContext";
import { useTerritoryMasters, TerritoryFilterSelects, matchesTerritory } from "../../components/TerritoryFilter";
import { useReceiptVisibility } from "../../lib/receiptVisibility";   // Phase 169
import { ReceiptHiddenBadge, ReceiptHiddenBanner } from "../../components/ReceiptVisibilityNotice";   // Phase 169

const isoDay = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

const PERIODS = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "7d", label: "7 days" },
  { key: "month", label: "This month" },
  { key: "custom", label: "Custom" },
];

export default function MyCollections() {
  const { code } = useParams();
  const { auth } = useAuth();
  const myId = auth?.user?.id;
  const [payments, setPayments] = useState(null);
  const [filter, setFilter] = useState("all"); // all | mine | team
  const [period, setPeriod] = useState("all"); // all | today | 7d | month | custom
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [mode, setMode] = useState("all");
  const [search, setSearch] = useState("");
  const [territory, setTerritory] = useState({ zoneId: "", wardId: "", mohallaId: "" });
  const [showFilters, setShowFilters] = useState(false);
  const territoryMasters = useTerritoryMasters(code);

  const live = useLiveCollections(code);   // Phase 149: receipts recorded on any device show up here
  const rv = useReceiptVisibility(code);   // Phase 169: may this collector open receipts right now?
  useEffect(() => {
    api.get(`/t/${code}/payments`, { params: { limit: 500 } })
      .then((r) => setPayments(r.data)).catch(() => {});
  }, [code, live]);

  const modes = useMemo(() => {
    if (!payments) return [];
    return [...new Set(payments.map((p) => p.payment_mode).filter(Boolean))].sort();
  }, [payments]);

  const { rangeFrom, rangeTo } = useMemo(() => {
    const today = new Date();
    if (period === "today") { const d = isoDay(today); return { rangeFrom: d, rangeTo: d }; }
    if (period === "7d") {
      const from = new Date(today); from.setDate(from.getDate() - 6);
      return { rangeFrom: isoDay(from), rangeTo: isoDay(today) };
    }
    if (period === "month") {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { rangeFrom: isoDay(from), rangeTo: isoDay(today) };
    }
    if (period === "custom") return { rangeFrom: fromDate || null, rangeTo: toDate || null };
    return { rangeFrom: null, rangeTo: null };
  }, [period, fromDate, toDate]);

  if (!payments) return <Spinner />;

  const hasTeam = payments.some((p) => p.collected_by && p.collected_by !== myId);
  const s = search.trim().toLowerCase();
  const shown = payments.filter((p) => {
    if (filter === "mine" && p.collected_by !== myId) return false;
    if (filter === "team" && p.collected_by === myId) return false;
    const day = (p.payment_date || "").slice(0, 10);
    if (rangeFrom && day < rangeFrom) return false;
    if (rangeTo && day > rangeTo) return false;
    if (mode !== "all" && p.payment_mode !== mode) return false;
    if (!matchesTerritory(p, territory)) return false;
    if (s) {
      const hay = `${p.receipt_number || ""} ${p.pin || ""} ${p.owner_name || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
  const total = shown.reduce((sum, p) => sum + p.amount, 0);
  const territoryActive = territory.zoneId || territory.wardId || territory.mohallaId ? 1 : 0;
  const activeFilterCount = (period !== "all" ? 1 : 0) + (mode !== "all" ? 1 : 0) + (s ? 1 : 0) + territoryActive;

  const clearFilters = () => {
    setPeriod("all"); setFromDate(""); setToDate(""); setMode("all"); setSearch("");
    setTerritory({ zoneId: "", wardId: "", mohallaId: "" });
  };

  const chips = [
    { key: "all", label: "All" },
    { key: "mine", label: "Mine" },
    { key: "team", label: "Team" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold font-heading">My collections</h1>
          <p className="text-sm text-muted-foreground" data-testid="collections-summary">
            {shown.length} receipt{shown.length === 1 ? "" : "s"} • {fmtINR(total)} collected
          </p>
        </div>
        <Button variant={showFilters || activeFilterCount > 0 ? "default" : "outline"} size="sm" className="h-9 shrink-0"
          onClick={() => setShowFilters((v) => !v)} data-testid="collections-filter-button">
          <Filter className="h-4 w-4 mr-1.5" /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
        </Button>
      </div>

      {showFilters && (
        <Card className="shadow-sm" data-testid="collections-filter-panel">
          <CardContent className="p-3 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Period</Label>
              <div className="flex flex-wrap gap-1.5">
                {PERIODS.map((p) => (
                  <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      period === p.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:bg-muted"
                    }`}
                    data-testid={`collections-period-${p.key}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {period === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <Input type="date" className="h-10" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                    data-testid="collections-from-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Input type="date" className="h-10" value={toDate} onChange={(e) => setToDate(e.target.value)}
                    data-testid="collections-to-date" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Payment mode</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="h-10" data-testid="collections-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All modes</SelectItem>
                    {modes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input className="h-10 pl-9" placeholder="Receipt no, PIN or owner..."
                    value={search} onChange={(e) => setSearch(e.target.value)} data-testid="collections-search-input" />
                </div>
              </div>
            </div>
            <TerritoryFilterSelects masters={territoryMasters} value={territory} onChange={setTerritory}
              testIdPrefix="collections" />
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={clearFilters}
                data-testid="collections-clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {hasTeam && (
        <div className="flex gap-2" data-testid="collections-filter-chips">
          {chips.map((c) => (
            <button key={c.key} type="button" onClick={() => setFilter(c.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                filter === c.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`collections-filter-${c.key}`}>
              {c.label}
            </button>
          ))}
        </div>
      )}
      {shown.length === 0 ? (
        <EmptyState title={activeFilterCount > 0 ? "No receipts match these filters" : "No collections yet"}
          subtitle={activeFilterCount > 0 ? "Try changing the period, mode or search." : undefined} />
      ) : (
        <div className="space-y-3" data-testid="collector-collections-list">
          <ReceiptHiddenBanner rv={rv} testId="collector-receipts-hidden-banner" />
          {shown.map((p) => {
            // Phase 169: the server flags whether THIS receipt may be opened by this role (toggle + window + payment-date gate)
            const canOpen = p.receipt_visible !== false && rv.canView(p.payment_date);
            const card = (
              <Card className={`shadow-sm mb-3 ${canOpen ? "active:scale-[0.99] transition-transform" : "border-dashed bg-muted/30"}`} data-testid={`collector-receipt-card-${p.receipt_number}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-medium">{p.receipt_number}{!canOpen && <span className="ml-2 align-middle inline-flex"><ReceiptHiddenBadge testId={`collector-receipt-hidden-${p.receipt_number}`} /></span>}</p>
                    <p className="text-sm truncate">{p.owner_name} • <span className="font-mono">{p.pin}</span></p>
                    <p className="text-xs text-muted-foreground">{p.payment_mode} • {fmtDate(p.payment_date)} {p.payment_time}</p>
                    {p.source === "citizen_portal" && (
                      <Badge variant="outline" className="mt-1 mr-1 text-[10px] gap-1 bg-accent text-accent-foreground border-primary/20" data-testid={`portal-receipt-badge-${p.receipt_number}`}>
                        <Globe className="h-3 w-3" /> Paid online by citizen · credited to you
                      </Badge>
                    )}
                    {p.collected_by && p.collected_by !== myId && (
                      <Badge variant="outline" className="mt-1 text-[10px] gap-1" data-testid={`team-receipt-badge-${p.receipt_number}`}>
                        <Users className="h-3 w-3" /> <StaffLabel code={code} userId={p.collected_by} name={p.collected_by_name} empCode={p.collected_by_employee_id} fallback="Sub-user" />
                      </Badge>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold tabular-nums">{fmtINR(p.amount)}</p>
                    <StatusBadge status={p.status} />
                  </div>
                </CardContent>
              </Card>
            );
            return canOpen
              ? <Link key={p.id} to={`/${code}/receipt/${p.receipt_number}`} data-testid={`collector-receipt-link-${p.receipt_number}`}>{card}</Link>
              : <div key={p.id} aria-disabled="true">{card}</div>;
          })}
        </div>
      )}
    </div>
  );
}
